import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { format } from "date-fns";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend
} from "recharts";
import {
  Calendar,
  ChevronLeft,
  ChevronRight,
  ClipboardList,
  Loader2,
  TrendingUp,
  BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";

import { API } from "./utils";


function SubscriptionCalendarTab() {
  const [historicalData, setHistoricalData] = useState({ daily_revenue: [], monthly_totals: [] });
  const [projectedData, setProjectedData] = useState({ projected_revenue: [], monthly_totals: [], active_subscriptions_count: 0 });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedDayTransactions, setSelectedDayTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
  const [chartData, setChartData] = useState({ monthly: [], current_active_subs: 0, current_mrr: 0 });
  const [chartRange, setChartRange] = useState('12'); // months to show

  const API = process.env.REACT_APP_BACKEND_URL;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Fetch transactions for a specific date (historical)
  const fetchTransactionsForDate = async (date) => {
    setLoadingTransactions(true);
    try {
      const response = await axios.get(`${API}/api/subscription-calendar/transactions-by-date?date=${date}`);
      setSelectedDayTransactions(response.data.transactions || []);
    } catch (err) {
      console.error('Failed to fetch transactions for date:', err);
      setSelectedDayTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  // Handle day selection
  const handleDaySelect = (dayInfo) => {
    setSelectedDay(dayInfo);
    setSelectedDayTransactions([]);
    
    // If it's a historical date, fetch the transactions
    if (dayInfo.data?.type === 'historical') {
      fetchTransactionsForDate(dayInfo.date);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [historicalRes, projectedRes, summaryRes, chartRes] = await Promise.all([
        axios.get(`${API}/api/subscription-calendar/historical?months_back=12`),
        axios.get(`${API}/api/subscription-calendar/projected?months_ahead=6`),
        axios.get(`${API}/api/subscription-calendar/summary`),
        axios.get(`${API}/api/subscription-calendar/chart-data?months_back=24`)
      ]);
      setHistoricalData(historicalRes.data);
      setProjectedData(projectedRes.data);
      setSummary(summaryRes.data);
      setChartData(chartRes.data);
    } catch (err) {
      toast.error("Failed to load calendar data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Build calendar data map
  const calendarDataMap = useMemo(() => {
    const map = {};
    
    // Add historical data
    historicalData.daily_revenue.forEach(item => {
      map[item.date] = {
        ...item,
        type: 'historical'
      };
    });
    
    // Add projected data
    projectedData.projected_revenue.forEach(item => {
      if (!map[item.date]) {
        map[item.date] = {
          date: item.date,
          total: item.total,
          transaction_count: item.subscription_count,
          type: 'projected',
          subscriptions: item.subscriptions
        };
      }
    });
    
    return map;
  }, [historicalData, projectedData]);

  // Generate calendar days for current month
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startPadding = firstDay.getDay();
    
    const days = [];
    
    // Add padding for days before first of month
    for (let i = 0; i < startPadding; i++) {
      days.push({ empty: true });
    }
    
    // Add days of the month
    for (let d = 1; d <= lastDay.getDate(); d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const data = calendarDataMap[dateStr];
      days.push({
        day: d,
        date: dateStr,
        data: data,
        isToday: dateStr === new Date().toISOString().split('T')[0]
      });
    }
    
    return days;
  }, [currentMonth, calendarDataMap]);

  // Calculate month totals
  const monthStats = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const monthStr = `${year}-${String(month + 1).padStart(2, '0')}`;
    
    let historicalTotal = 0;
    let projectedTotal = 0;
    let historicalCount = 0;
    let projectedCount = 0;
    
    Object.entries(calendarDataMap).forEach(([date, data]) => {
      if (date.startsWith(monthStr)) {
        if (data.type === 'historical') {
          historicalTotal += data.total;
          historicalCount += data.transaction_count || 0;
        } else {
          projectedTotal += data.total;
          projectedCount += data.transaction_count || 0;
        }
      }
    });
    
    return { historicalTotal, projectedTotal, historicalCount, projectedCount };
  }, [currentMonth, calendarDataMap]);

  const prevMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentMonth(new Date());
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards - Row 1 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg border">
          <div className="text-xs text-slate-500 uppercase tracking-wide">This Month (Actual)</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">
            {formatCurrency(summary?.historical?.this_month?.total || 0)}
          </div>
          <div className="text-xs text-slate-500">{summary?.historical?.this_month?.count || 0} transactions</div>
        </div>
        <div className="bg-white p-4 rounded-lg border">
          <div className="text-xs text-slate-500 uppercase tracking-wide">Last Month</div>
          <div className="text-2xl font-bold text-slate-900 mt-1">
            {formatCurrency(summary?.historical?.last_month?.total || 0)}
          </div>
          <div className="text-xs text-slate-500">{summary?.historical?.last_month?.count || 0} transactions</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-blue-200 bg-blue-50">
          <div className="text-xs text-blue-600 uppercase tracking-wide">Monthly Recurring (MRR)</div>
          <div className="text-2xl font-bold text-blue-900 mt-1">
            {formatCurrency(summary?.projected?.mrr || 0)}
          </div>
          <div className="text-xs text-blue-600">Projected monthly</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-green-200 bg-green-50">
          <div className="text-xs text-green-600 uppercase tracking-wide">Active Subscriptions</div>
          <div className="text-2xl font-bold text-green-900 mt-1">
            {projectedData.active_subscriptions_count || 0}
          </div>
          <div className="text-xs text-green-600">In Stripe</div>
        </div>
      </div>

      {/* Summary Cards - Row 2: YTD & School Year */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <div className="bg-white p-4 rounded-lg border border-purple-200 bg-purple-50">
          <div className="text-xs text-purple-600 uppercase tracking-wide">Year to Date ({new Date().getFullYear()})</div>
          <div className="text-2xl font-bold text-purple-900 mt-1">
            {formatCurrency(summary?.historical?.ytd?.total || 0)}
          </div>
          <div className="text-xs text-purple-600">{summary?.historical?.ytd?.count || 0} transactions</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-amber-200 bg-amber-50">
          <div className="text-xs text-amber-700 uppercase tracking-wide">School Year 2025-2026</div>
          <div className="text-2xl font-bold text-amber-900 mt-1">
            {formatCurrency(summary?.historical?.school_year_2025_2026?.total || 0)}
          </div>
          <div className="text-xs text-amber-700">Sep '25 - Aug '26 ({summary?.historical?.school_year_2025_2026?.count || 0} transactions)</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-slate-200">
          <div className="text-xs text-slate-500 uppercase tracking-wide">School Year 2024-2025</div>
          <div className="text-2xl font-bold text-slate-700 mt-1">
            {formatCurrency(summary?.historical?.school_year_2024_2025?.total || 0)}
          </div>
          <div className="text-xs text-slate-500">Sep '24 - Aug '25 ({summary?.historical?.school_year_2024_2025?.count || 0} transactions)</div>
        </div>
      </div>

      {/* Charts Section */}
      {chartData.monthly.length > 0 && (() => {
        const displayData = chartData.monthly.slice(-parseInt(chartRange));
        const formatCurrencyShort = (val) => val >= 1000 ? `$${(val/1000).toFixed(1)}k` : `$${val}`;
        
        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Revenue & Subscriptions
              </h2>
              <Select value={chartRange} onValueChange={setChartRange}>
                <SelectTrigger className="w-[140px] h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="6">Last 6 months</SelectItem>
                  <SelectItem value="12">Last 12 months</SelectItem>
                  <SelectItem value="24">Last 24 months</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Monthly Revenue Chart */}
              <div className="bg-white rounded-lg border p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4 text-emerald-600" />
                  Monthly Revenue
                </h3>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={displayData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="label" 
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      tickFormatter={(val) => val?.replace(' 20', " '")}
                    />
                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} tickFormatter={formatCurrencyShort} />
                    <RechartsTooltip 
                      formatter={(value) => [`$${value.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 'Revenue']}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }}
                    />
                    <Bar dataKey="revenue" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Active Customers (Subscriptions) Chart */}
              <div className="bg-white rounded-lg border p-4">
                <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-blue-600" />
                  Active Subscribers
                  <Badge className="text-[10px] bg-blue-100 text-blue-700 ml-auto">{chartData.current_active_subs} current</Badge>
                </h3>
                <ResponsiveContainer width="100%" height={260}>
                  <AreaChart data={displayData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorSubs" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="label" 
                      tick={{ fontSize: 11, fill: '#64748b' }}
                      tickFormatter={(val) => val?.replace(' 20', " '")}
                    />
                    <YAxis tick={{ fontSize: 11, fill: '#64748b' }} />
                    <RechartsTooltip 
                      formatter={(value, name) => [value, name === 'active_customers' ? 'Active Subscribers' : name]}
                      contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '13px' }}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="active_customers" 
                      stroke="#3b82f6" 
                      strokeWidth={2}
                      fill="url(#colorSubs)" 
                      name="Active Subscribers"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Calendar Header */}
      <div className="bg-white rounded-lg border p-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={prevMonth}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" size="sm" onClick={goToToday}>Today</Button>
            <Button variant="outline" size="sm" onClick={nextMonth}>
              <ChevronRight className="w-4 h-4" />
            </Button>
            <h2 className="text-lg font-bold text-slate-900 ml-2">
              {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h2>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-xs">
              <span className="w-3 h-3 rounded bg-green-500"></span>
              <span className="text-slate-600">Actual</span>
              <span className="w-3 h-3 rounded bg-blue-400 ml-2"></span>
              <span className="text-slate-600">Projected</span>
            </div>
            <div className="flex items-center gap-1 border rounded-md p-1">
              <Button 
                variant={viewMode === 'calendar' ? 'secondary' : 'ghost'} 
                size="sm" 
                onClick={() => setViewMode('calendar')}
                className="h-7 px-2"
              >
                <Calendar className="w-4 h-4" />
              </Button>
              <Button 
                variant={viewMode === 'list' ? 'secondary' : 'ghost'} 
                size="sm" 
                onClick={() => setViewMode('list')}
                className="h-7 px-2"
              >
                <ClipboardList className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Month Stats */}
        <div className="flex gap-4 mb-4 text-sm">
          <div className="flex items-center gap-2">
            <span className="text-slate-500">Month Actual:</span>
            <span className="font-semibold text-green-600">{formatCurrency(monthStats.historicalTotal)}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-500">Month Projected:</span>
            <span className="font-semibold text-blue-600">{formatCurrency(monthStats.projectedTotal)}</span>
          </div>
        </div>

        {viewMode === 'calendar' ? (
          <>
            {/* Calendar Grid - Day Headers */}
            <div className="grid grid-cols-7 gap-1 mb-1">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="text-center text-xs font-medium text-slate-500 py-2">
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar Grid - Days */}
            <div className="grid grid-cols-7 gap-1">
              {calendarDays.map((dayInfo, idx) => (
                <div
                  key={idx}
                  className={`
                    min-h-[80px] p-1 rounded border text-xs
                    ${dayInfo.empty ? 'bg-slate-50 border-transparent' : 'bg-white hover:bg-slate-50 cursor-pointer'}
                    ${dayInfo.isToday ? 'border-blue-500 border-2' : 'border-slate-200'}
                  `}
                  onClick={() => !dayInfo.empty && dayInfo.data && handleDaySelect(dayInfo)}
                >
                  {!dayInfo.empty && (
                    <>
                      <div className={`font-medium ${dayInfo.isToday ? 'text-blue-600' : 'text-slate-700'}`}>
                        {dayInfo.day}
                      </div>
                      {dayInfo.data && (
                        <div className={`
                          mt-1 p-1 rounded text-[10px] font-medium truncate
                          ${dayInfo.data.type === 'historical' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-blue-100 text-blue-700'}
                        `}>
                          {formatCurrency(dayInfo.data.total)}
                          <div className="text-[9px] opacity-75">
                            {dayInfo.data.transaction_count || dayInfo.data.subscription_count} {dayInfo.data.type === 'historical' ? 'tx' : 'subs'}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ))}
            </div>
          </>
        ) : (
          /* List View */
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {Object.entries(calendarDataMap)
              .filter(([date]) => {
                const d = new Date(date);
                return d.getMonth() === currentMonth.getMonth() && d.getFullYear() === currentMonth.getFullYear();
              })
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([date, data]) => (
                <div 
                  key={date} 
                  className={`
                    flex items-center justify-between p-3 rounded-lg border cursor-pointer hover:bg-slate-50
                    ${data.type === 'historical' ? 'border-green-200' : 'border-blue-200'}
                  `}
                  onClick={() => handleDaySelect({ date, data })}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${data.type === 'historical' ? 'bg-green-500' : 'bg-blue-500'}`}></div>
                    <div>
                      <div className="font-medium text-slate-900">
                        {new Date(date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                      </div>
                      <div className="text-xs text-slate-500">
                        {data.transaction_count || data.subscription_count} {data.type === 'historical' ? 'transactions' : 'renewals'}
                      </div>
                    </div>
                  </div>
                  <div className={`font-bold ${data.type === 'historical' ? 'text-green-600' : 'text-blue-600'}`}>
                    {formatCurrency(data.total)}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      {/* Selected Day Details Dialog */}
      <Dialog open={!!selectedDay} onOpenChange={() => setSelectedDay(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedDay && new Date(selectedDay.date + 'T12:00:00').toLocaleDateString('en-US', { 
                weekday: 'long', 
                month: 'long', 
                day: 'numeric',
                year: 'numeric'
              })}
            </DialogTitle>
            <DialogDescription>
              {selectedDay?.data?.type === 'historical' 
                ? 'Actual subscription revenue recorded' 
                : 'Projected subscription renewals'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <div className={`
              text-3xl font-bold mb-4
              ${selectedDay?.data?.type === 'historical' ? 'text-green-600' : 'text-blue-600'}
            `}>
              {formatCurrency(selectedDay?.data?.total || 0)}
            </div>
            
            {selectedDay?.data?.type === 'projected' && selectedDay?.data?.subscriptions && (
              <div className="space-y-2">
                <div className="text-sm font-medium text-slate-700 mb-2">Renewal Details:</div>
                {selectedDay.data.subscriptions.map((sub, idx) => (
                  <div key={idx} className="flex justify-between items-center p-2 bg-slate-50 rounded text-sm">
                    <span className="text-slate-700">{sub.customer_name}</span>
                    <span className="font-medium text-slate-900">{formatCurrency(sub.amount)}</span>
                  </div>
                ))}
              </div>
            )}
            
            {selectedDay?.data?.type === 'historical' && (
              <div className="space-y-2">
                <div className="text-sm font-medium text-slate-700 mb-2">Transaction Details:</div>
                {loadingTransactions ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                  </div>
                ) : selectedDayTransactions.length > 0 ? (
                  <div className="max-h-[300px] overflow-y-auto space-y-2">
                    {selectedDayTransactions.map((tx, idx) => (
                      <div key={idx} className={`flex justify-between items-center p-2 rounded text-sm border ${
                        tx.status === 'succeeded' 
                          ? 'bg-green-50 border-green-100' 
                          : 'bg-red-50 border-red-100'
                      }`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className={`font-medium ${tx.status === 'succeeded' ? 'text-slate-700' : 'text-red-700'}`}>
                              {tx.customer_name || 'Unknown'}
                            </span>
                            {tx.status !== 'succeeded' && (
                              <Badge variant="destructive" className="text-[10px] px-1 py-0">
                                Failed
                              </Badge>
                            )}
                          </div>
                          <div className="text-xs text-slate-500 truncate">{tx.description || 'No description'}</div>
                        </div>
                        <span className={`font-medium ml-2 ${tx.status === 'succeeded' ? 'text-green-700' : 'text-red-400 line-through'}`}>
                          {formatCurrency(tx.amount)}
                        </span>
                      </div>
                    ))}
                    <div className="border-t pt-2 mt-2 flex justify-between items-center text-sm font-medium">
                      <span className="text-slate-600">
                        Succeeded: {selectedDayTransactions.filter(t => t.status === 'succeeded').length} of {selectedDayTransactions.length}
                      </span>
                      <span className="text-green-700">{formatCurrency(selectedDay?.data?.total || 0)}</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-slate-500">No transactions found for this date</div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Monthly Breakdown Tables */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Historical Monthly */}
        <div className="bg-white rounded-lg border p-4">
          <h3 className="font-bold text-slate-900 mb-1 flex items-center gap-2">
            <span className="w-3 h-3 rounded bg-green-500"></span>
            Actual Revenue Received
          </h3>
          <p className="text-xs text-slate-500 mb-3">From completed Stripe transactions (Rental Revenue)</p>
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {historicalData.monthly_totals.map((item, idx) => {
              const itemMonth = new Date(item.month);
              const isCurrentMonth = itemMonth.getMonth() === new Date().getMonth() && 
                                     itemMonth.getFullYear() === new Date().getFullYear();
              return (
                <div key={idx} className="flex justify-between items-center p-2 hover:bg-slate-50 rounded">
                  <span className="text-sm text-slate-600">
                    {new Date(item.month).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    {isCurrentMonth && <span className="ml-1 text-xs text-green-600">(month-to-date)</span>}
                  </span>
                  <div className="text-right">
                    <div className="font-medium text-green-600">{formatCurrency(item.total)}</div>
                    <div className="text-xs text-slate-400">{item.transaction_count} tx</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Projected Monthly */}
        <div className="bg-white rounded-lg border p-4">
          <h3 className="font-bold text-slate-900 mb-1 flex items-center gap-2">
            <span className="w-3 h-3 rounded bg-blue-500"></span>
            Expected Subscription Renewals
          </h3>
          <p className="text-xs text-slate-500 mb-3">Based on active Stripe subscriptions billing dates</p>
          <div className="space-y-2 max-h-[300px] overflow-y-auto">
            {projectedData.monthly_totals.map((item, idx) => {
              const itemMonth = new Date(item.month + '-01');
              const isCurrentMonth = itemMonth.getMonth() === new Date().getMonth() && 
                                     itemMonth.getFullYear() === new Date().getFullYear();
              return (
                <div key={idx} className="flex justify-between items-center p-2 hover:bg-slate-50 rounded">
                  <span className="text-sm text-slate-600">
                    {new Date(item.month + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                    {isCurrentMonth && <span className="ml-1 text-xs text-blue-600">(remaining)</span>}
                  </span>
                  <div className="text-right">
                    <div className="font-medium text-blue-600">{formatCurrency(item.total)}</div>
                    <div className="text-xs text-slate-400">{item.subscription_count} renewals</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}


export default SubscriptionCalendarTab;
